import { useAuth } from "@lib/auth/useAuth";
import { getPagePathByLocale, logInPage } from "@lib/constants/pagePaths";
import { useApi } from "@lib/helpers/useApi";
import { useToast } from "@ecco/ui/use-toast";
import { type FormikHelpers } from "formik";
import { Notification } from "./components/Notification";
import { CHANGE_PASSWORD_API_PATH, TOAST_DURATION } from "./constants";
import type { Headers, Values } from "./types";
import { useLocale } from "next-intl";
import { useApplyLocalizedErrorMessages } from "@lib/validators/yupConfig";

export const changePasswordProfileWrapper = "change-password-profile-wrapper";

type Props = {
    handleClose?: () => void;
    headers: Headers;
};

export const useChangePasswordForm = ({ handleClose, headers }: Props) => {
    const locale = useLocale();
    const { signOut } = useAuth();
    const { toast } = useToast();

    useApplyLocalizedErrorMessages();

    const onClose = () => handleClose?.();

    const errorHeaders = {
        passwordsDontMatch: headers.passwordsDontMatch,
        requiredField: headers.requiredFieldError,
        passwordRulesError: headers.passwordRulesError,
    };

    const onToastClose = async () => {
        await signOut(getPagePathByLocale(logInPage, locale));
    };

    const showToast = () => {
        toast({
            position: "top",
            duration: TOAST_DURATION,
            onCloseComplete: onToastClose,
            render: () => (
                <Notification
                    onClose={() => toast.closeAll()}
                    description={headers.redirectToLogIn}
                    title={headers.passwordWasUpdated}
                />
            ),
        });
    };

    const mutation = useApi.useMutate(`${CHANGE_PASSWORD_API_PATH}`, {
        method: "POST",
        onSuccess: showToast,
    });

    const submitHandler = async (values: Values, { setErrors }: FormikHelpers<Values>) => {
        try {
            const payload = {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            };
            await mutation.mutateAsync(payload);
        } catch {
            setErrors({ currentPassword: headers.wrongPassword });
        }
    };

    return {
        submitHandler,
        errorHeaders,
        onClose,
    };
};
